import { StrategyDetailsPageGetDueDiligenceRecordQuery } from '@/__generated__/graphql';
import { ApprovedIcon } from '@/assets/icons/info-icons/approved';
import { RejectedBannerIcon } from '@/assets/icons/info-icons/rejectedBanner';
import { DeleteBucketIcon } from '@/assets/static/icons/delete-bucket';
import { Banner } from '@/components/basicComponents/banner';
import ModalLoader from '@/components/basicComponents/modal/modalLoader';
import ModalWrappedContent from '@/components/basicComponents/modal/modalWrappedContent';
import { useErrorMessages } from '@/hooks/i18n';
import { themeVar } from '@/styles/theme';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import { useState } from 'react';
import styled from 'styled-components';
import { STRATEGY_DETAILS_PAGE_DELETE_DUE_DILIGENCE_RECORD_MUTATION } from '../../queries';

interface DeleteDueDiligenceRecordProps {
  isOpen: boolean;
  recordId: string;
  dueDiligenceRecordRefetch: () => Promise<ApolloQueryResult<StrategyDetailsPageGetDueDiligenceRecordQuery>>;
  onClose: () => void;
}

const DeleteDueDiligenceRecord = ({ isOpen, recordId, dueDiligenceRecordRefetch, onClose }: DeleteDueDiligenceRecordProps) => {
  const { default: defaultErrorMessage } = useErrorMessages();

  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);

  const [deleteRecord, { loading: deleteRecordLoading }] = useMutation(STRATEGY_DETAILS_PAGE_DELETE_DUE_DILIGENCE_RECORD_MUTATION);

  const handleDeleteDueDiligenceRecord = async () => {
    try {
      setErrorMessage(null);

      await deleteRecord({
        variables: {
          id: recordId
        }
      });

      setShowSuccessDialog(true);
    } catch {
      setErrorMessage(defaultErrorMessage);
    }
  };

  const handleCloseSuccessDialog = () => {
    dueDiligenceRecordRefetch();
    setShowSuccessDialog(false);
    onClose();
  };

  if (showSuccessDialog) {
    return (
      <ModalWrappedContent
        isOpen={isOpen}
        onClose={handleCloseSuccessDialog}
        confirmButton={{
          name: 'OK',
          onClick: handleCloseSuccessDialog
        }}
        showCancelButton={false}
      >
        <ModalBody>
          <ApprovedIcon width={60} height={60} />
          <ModalTitle>Success</ModalTitle>
          <ModalDescription>The Due Diligence record has been deleted.</ModalDescription>
        </ModalBody>
      </ModalWrappedContent>
    );
  }
  return (
    <ModalWrappedContent
      isOpen={isOpen}
      onClose={onClose}
      confirmButton={{
        name: 'Delete',
        onClick: handleDeleteDueDiligenceRecord,
        disabled: deleteRecordLoading
      }}
      showCancelButton={!deleteRecordLoading}
      showCloseIcon={!deleteRecordLoading}
      isUseOutsideClick={!deleteRecordLoading}
    >
      <ModalBody>
        <DeleteBucketIcon fill={themeVar('font.disabled')} />
        <ModalTitle>Please confirm that you want to delete this Due Diligence record.</ModalTitle>
        {deleteRecordLoading ? (
          <ModalLoader />
        ) : (
          <>
            {errorMessage && (
              <ErrorBannerWrapper>
                <Banner
                  icon={<RejectedBannerIcon fill={themeVar('context.light')} />}
                  title="Error"
                  description={errorMessage}
                  bgColor={themeVar('context.error')}
                />
              </ErrorBannerWrapper>
            )}
            <ModalDescription>There is no undo for this operation.</ModalDescription>
          </>
        )}
      </ModalBody>
    </ModalWrappedContent>
  );
};

export default DeleteDueDiligenceRecord;

const ModalBody = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-direction: column;
  gap: 10px;
  margin-top: 10px;
`;

const ModalTitle = styled.div`
  font-family: Blinker;
  font-size: 33px;
  font-style: normal;
  font-weight: 400;
  line-height: 46.2px;
  text-align: center;
  color: ${themeVar('font.strong')};
`;

const ModalDescription = styled.div`
  font-size: 16px;
  font-style: normal;
  text-align: center;
  color: ${themeVar('font.base')};
`;

const ErrorBannerWrapper = styled.div`
  margin-bottom: 10px;
`;
